import { apiWithBody, ModuleFE_BaseApi, StorageKey, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { ApiDef_Discussion, DBDef_Discussion } from '@app/hcs-shared';
import { ModuleFE_Message, ModuleFE_Topic } from '@nu-art/ts-messaging-frontend';
import { BadImplementationException, dbObjectToId, RuntimeModules, ThisShouldNotHappenException } from '@nu-art/ts-common';
import { DiscussionType_Chat } from '@app/hcs-shared/_entity/discussion/consts';
import { CollectionEntityMapper } from '../../entities/CollectionEntityMapper.js';
export const dispatch_onDiscussionsUpdated = new ThunderDispatcherV3('__onDiscussionsUpdated');
export const dispatch_discussionViewOptionsChanged = new ThunderDispatcher('__discussionsViewOptionsChanged');
const dispatch_showDiscussions = new ThunderDispatcher('__showDiscussions');
export const storage_FilterSelected = new StorageKey('messaging_discussions-view_filter-selected').withstandDeletion();
export class ModuleFE_Discussion_Class extends ModuleFE_BaseApi {
    dbEntityToLabel = {};
    showResolvedDiscussions = false;
    _v1;
    constructor() {
        super(DBDef_Discussion, dispatch_onDiscussionsUpdated);
        this._v1 = {
            createNewDiscussion: apiWithBody(ApiDef_Discussion._v1.createNewDiscussion, async (response) => {
                await ModuleFE_Topic.onEntryUpdated(response.topic, response.topic);
                await this.onEntryUpdated(response.discussion, response.discussion);
                await ModuleFE_Message.onEntryUpdated(response.message, response.message);
            }),
            deleteDiscussion: apiWithBody(ApiDef_Discussion._v1.deleteDiscussion, async (response) => {
                if (response.deletedDiscussion)
                    await this.onEntryDeleted(response.deletedDiscussion);
                if (response.deletedTopic)
                    await ModuleFE_Topic.onEntriesDeleted([response.deletedTopic]);
                if (response.deletedMessages)
                    await ModuleFE_Message.onEntriesDeleted(response.deletedMessages);
            }),
        };
    }
    getFirstMessageForDiscussion(discussion) {
        return discussion && ModuleFE_Message.cache.filter(msg => msg.topicId === discussion.topicId).reduce((firstMsg, currentMsg) => {
            return firstMsg && firstMsg.__created < currentMsg.__created ? firstMsg : currentMsg;
        }, undefined);
    }
    getLastMessageForDiscussion(discussion) {
        return discussion && ModuleFE_Message.cache.filter(msg => msg.topicId === discussion.topicId).reduce((lastMsg, currentMsg) => {
            return lastMsg && lastMsg.__created > currentMsg.__created ? lastMsg : currentMsg;
        }, undefined);
    }
    getRelevantModuleByCollectionName(collectionName) {
        const relevantModuleByCollectionName = RuntimeModules()
            .find(module => !!module.getCollectionName && module.getCollectionName() === collectionName);
        if (!relevantModuleByCollectionName) {
            this.logErrorBold(`Failed to find collection module for collectionName: ${collectionName}!`);
            return;
        }
        return relevantModuleByCollectionName;
    }
    getReferencedObjectByCollectionRef(collectionName, refId) {
        const collectionModule = this.getRelevantModuleByCollectionName(collectionName);
        return collectionModule?.cache.unique(refId);
    }
    getTopicsByObjectRef(collectionName, refId) {
        return ModuleFE_Topic.cache.filter(topic => topic.type === collectionName && topic.refId === refId);
    }
    async addMessageToDiscussion(discussionId, msg) {
        const discussion = this.cache.unique(discussionId);
        let topic;
        if (!discussion) {
            topic = await ModuleFE_Topic.v1.upsert({ refId: 'test-refId', type: 'test-type' }).executeSync();
            await this.v1.upsert({
                _id: discussionId,
                topicId: topic._id,
                type: DiscussionType_Chat
            }).executeSync();
        }
        else
            topic = ModuleFE_Topic.cache.unique(discussion.topicId);
        if (!topic)
            throw new ThisShouldNotHappenException('Discussions should always have topicId.');
        await ModuleFE_Message.createMessage(topic._id, msg);
    }
    openDiscussions(props) {
        dispatch_showDiscussions.dispatchUI(props);
    }
    registerLabelFromEntityConverter(dbName, converter) {
        if (this.dbEntityToLabel[dbName])
            throw new BadImplementationException(`A converter function for ${dbName} type was already provided, can't provide another!`);
        this.dbEntityToLabel[dbName] = converter;
    }
    getLabelFromEntity = (dbName, ref) => {
        const converter = this.dbEntityToLabel[dbName];
        if (!converter)
            throw new BadImplementationException(`A converter function for ${dbName} type messages was not provided! Should be added in ModuleFE's init function, see ModuleFE_Variables for reference.`);
        return converter(ref);
    };
    getLabelFromEntityId = (dbName, refId) => {
        const collectionModule = RuntimeModules().find(module => module.dbDef?.dbKey === dbName);
        if (!collectionModule)
            throw new BadImplementationException(`Could not find the collection module of type ${dbName}!`);
        const ref = collectionModule.cache.unique(refId);
        if (!ref)
            throw new BadImplementationException(`Could not find ref to get label from!`);
        return this.getLabelFromEntity(dbName, ref);
    };
    getLabelFromTopicId = (topicId) => {
        const dbTopic = ModuleFE_Topic.cache.unique(topicId);
        if (!dbTopic)
            throw new BadImplementationException(`Could not find topic to get label from!`);
        return this.getLabelFromEntityId(dbTopic.type, dbTopic.refId);
    };
    getEntityNameFromTopic(topicId) {
        const dbTopic = ModuleFE_Topic.cache.unique(topicId);
        if (!dbTopic)
            throw new BadImplementationException(`Could not find topic to get collection name from!`);
        return CollectionEntityMapper.getEntityMapByCollectionName(dbTopic.type).entityName;
    }
    shouldShowResolvedDiscussions() {
        return this.showResolvedDiscussions;
    }
    /*
    Returns boolean for state changed.
     */
    setShouldShowResolvedDiscussions(show) {
        if (show === this.showResolvedDiscussions)
            return false;
        this.showResolvedDiscussions = show;
        return true;
    }
    hasUnresolvedDiscussions = (refId, dbName) => {
        const topicIds = ModuleFE_Topic.cache.filter(topic => topic.refId === refId && topic.type === dbName).map(dbObjectToId);
        const discussions = this.cache.filter(discussion => topicIds.includes(discussion.topicId));
        return discussions.some(discussion => discussion.status !== 'resolved');
    };
}
export const ModuleFE_Discussion = new ModuleFE_Discussion_Class();
//# sourceMappingURL=ModuleFE_Discussion.js.map